import { dirname } from 'node:path';
import { readFileSync } from 'node:fs';
import { parse } from 'jsonc-parser';
import fg from 'fast-glob';
import type { Config } from '../../schema/types';
import { CONFIGURATION_FILENAMES } from '../../constants';

// cache the path to default configuration to avoid having to walk the file system multiple times
const configCache: Record<string, string> = {};

interface FindDefaultMicrofrontendPackageArgs {
  repositoryRoot: string;
  applicationName: string;
}

/**
 * Given a repository root and a package name, find the path to the package.json file with the
 * given name.
 *
 * This method uses globby to find all package.json files and then reads them in parallel
 */
function findDefaultMicrofrontendsPackages({
  repositoryRoot,
  applicationName,
}: FindDefaultMicrofrontendPackageArgs): string | null {
  try {
    // eslint-disable-next-line import/no-named-as-default-member
    const microfrontendsJsonPaths = fg.globSync(
      `**/{${CONFIGURATION_FILENAMES.join(',')}}`,
      {
        cwd: repositoryRoot,
        absolute: true,
        onlyFiles: true,
        followSymbolicLinks: false,
        ignore: ['**/node_modules/**', '**/.git/**'],
      },
    );

    const matchingPaths: string[] = [];
    for (const microfrontendsJsonPath of microfrontendsJsonPaths) {
      try {
        const microfrontendsJsonContent = readFileSync(
          microfrontendsJsonPath,
          'utf-8',
        );
        const microfrontendsJson = parse(microfrontendsJsonContent) as Config;

        if (microfrontendsJson.applications[applicationName]) {
          matchingPaths.push(microfrontendsJsonPath);
        }
      } catch (error) {
        // malformed json most likely, skip this file
      }
    }

    if (matchingPaths.length > 1) {
      throw new Error(
        `Found multiple default applications referencing "${applicationName}" in the repository, but only one is allowed.\n${matchingPaths.join('\n  • ')}`,
      );
    }

    if (matchingPaths.length === 0) {
      throw new Error(
        `Could not find default application with "applications.${applicationName}"`,
      );
    }

    const [packageJsonPath] = matchingPaths as [string];
    return dirname(packageJsonPath);
  } catch (error) {
    return null;
  }
}

/**
 * Given a repository root and a package name, find the path to the package directory with
 * a microfrontends config that contains the given name in its applications.
 */
export function findDefaultMicrofrontendsPackage(
  opts: FindDefaultMicrofrontendPackageArgs,
): string {
  // cache this with name to support multiple configurations in the same repository
  const cacheKey = `${opts.repositoryRoot}-${opts.applicationName}`;

  // Check if we have a cached result
  if (configCache[cacheKey]) {
    return configCache[cacheKey];
  }

  const result = findDefaultMicrofrontendsPackages(opts);

  if (!result) {
    throw new Error(
      'Error trying to resolve the main microfrontends configuration',
    );
  }

  // Cache the result
  configCache[cacheKey] = result;
  return result;
}
